import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Lock, PlayCircle, FileText, Download, Loader2, CheckCircle2, Circle, VideoOff, Pencil, Save, X, Award, XCircle, HelpCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { MemberShell, UpgradePlans } from "./Member";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ------------------------------ Toolbox index ------------------------------ */

export function ToolboxIndex() {
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [courseStatus, setCourseStatus] = useState(null);
  useEffect(() => {
    axios.get(`${API}/toolbox`).then(({ data }) => setModules(data.modules || []));
    if (user) {
      axios.get(`${API}/toolbox/course/status`)
        .then(({ data }) => setCourseStatus(data))
        .catch(() => setCourseStatus(null));
    }
  }, [user]);
  const paid = user?.tier === "paid_member";

  return (
    <MemberShell active="/member/toolbox">
      <div data-testid="toolbox-index-page">
        <div className="eyebrow mb-3">Toolbox</div>
        <h1 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight mb-3">Twelve modules. One curriculum.</h1>
        <p className="text-slate-600 max-w-2xl mb-8 leading-relaxed">
          The complete Social Impact Assessment curriculum from Andrea Kanaris - video lessons, editable templates, checklists and worked examples for every stage of a defensible SIA.
        </p>

        {!paid && (
          <div data-testid="toolbox-upgrade-banner" className="bg-brand-blue text-white p-8 mb-10">
            <div className="max-w-3xl">
              <div className="eyebrow text-brand-yellow mb-2">Membership</div>
              <h2 className="font-serif text-2xl leading-snug mb-2">Paid membership unlocks the full Member Hub.</h2>
              <p className="text-white/80 text-sm mb-4">All twelve modules, every editable template, community forum access and the Certificate of Completion are available to paid members. Secure Stripe checkout, cancel anytime.</p>
            </div>
            <UpgradePlans compact />
          </div>
        )}

        {courseStatus?.course_complete && (
          <div data-testid="toolbox-course-certificate-panel" className="bg-emerald-50 border border-emerald-200 p-6 mb-8 flex items-start gap-4">
            <Award className="w-10 h-10 text-emerald-700 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <div className="eyebrow text-emerald-700 mb-1">Full curriculum complete</div>
              <h2 className="font-serif text-2xl text-emerald-900 leading-snug mb-2">You've finished every module.</h2>
              <p className="text-sm text-emerald-800 mb-4">
                Download your Certificate of Completion and the accompanying Course Completion Record. The two documents share a certificate number and should be presented together.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  data-testid="download-course-certificate"
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/toolbox/certificate`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-brand-orange hover:bg-orange-600 text-white px-5 py-2.5 text-sm font-semibold"
                >
                  <Award className="w-4 h-4" /> Certificate of Completion
                </a>
                <a
                  data-testid="download-course-record"
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/toolbox/certificate/record`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-brand-blue hover:bg-slate-800 text-white px-5 py-2.5 text-sm font-semibold"
                >
                  <FileText className="w-4 h-4" /> Course Completion Record
                </a>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {modules.map((m) => {
            const done = m.completed_count || 0;
            const total = m.lesson_count || 0;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            return (
              <Link
                key={m.slug}
                to={`/member/toolbox/${m.slug}`}
                data-testid={`toolbox-module-${m.slug}`}
                className="group bg-white border border-slate-200 hover:border-brand-orange transition-colors p-6 relative"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-brand-orange">Module {m.number}</div>
                  {paid
                    ? <span className="text-[10px] uppercase tracking-widest font-bold text-brand-orange">Unlocked</span>
                    : <Lock className="w-4 h-4 text-slate-400" />}
                </div>
                <h3 className="font-serif text-xl text-brand-blue mb-3 leading-snug">{m.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">{m.summary}</p>
                <div className="text-xs text-slate-500 flex items-center gap-4">
                  <span className="flex items-center gap-1.5"><PlayCircle className="w-3.5 h-3.5" /> {m.lesson_count} lesson{m.lesson_count === 1 ? "" : "s"}</span>
                  {m.document_count > 0 && <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> {m.document_count} doc{m.document_count === 1 ? "" : "s"}</span>}
                </div>
                {total > 0 && (
                  <div data-testid={`toolbox-progress-${m.slug}`} className="mt-4">
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
                      <span>Your progress</span>
                      <span>{done}/{total}</span>
                    </div>
                    <div className="h-1 bg-slate-100">
                      <div className="h-1 bg-brand-orange" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )}
                <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-brand-orange absolute right-6 bottom-6 transition-colors" />
              </Link>
            );
          })}
        </div>
      </div>
    </MemberShell>
  );
}

/* ------------------------------ Module detail ------------------------------ */

export function ToolboxModule() {
  const { moduleSlug } = useParams();
  const [mod, setMod] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => axios.get(`${API}/toolbox/${moduleSlug}`)
    .then(({ data }) => setMod(data))
    .catch((e) => setErr(e?.response?.data?.detail || "Could not load module."));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [moduleSlug]);

  if (err) return <MemberShell active="/member/toolbox"><div className="text-slate-500">{err}</div></MemberShell>;
  if (!mod) return <MemberShell active="/member/toolbox"><div className="text-slate-500">Loading…</div></MemberShell>;

  return (
    <MemberShell active="/member/toolbox">
      <div data-testid={`toolbox-module-page-${mod.slug}`}>
        <Link to="/member/toolbox" data-testid="back-to-toolbox" className="text-xs uppercase tracking-widest font-semibold text-slate-500 hover:text-brand-orange inline-flex items-center gap-1.5">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Toolbox
        </Link>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest font-bold text-brand-orange">Module {mod.number}</span>
          {mod.unlocked
            ? <span className="text-[10px] uppercase tracking-widest font-bold text-brand-orange">Unlocked</span>
            : <span className="text-[10px] uppercase tracking-widest font-bold bg-slate-100 text-slate-700 px-2 py-1">Paid member</span>}
          {mod.is_admin && <span data-testid="admin-badge" className="text-[10px] uppercase tracking-widest font-bold bg-brand-yellow text-brand-blue px-2 py-1">Admin edit mode</span>}
        </div>
        <h1 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight mt-4">{mod.title}</h1>
        <p className="text-slate-600 mt-4 max-w-3xl leading-relaxed">{mod.summary}</p>

        {mod.unlocked && mod.total_lessons > 0 && (
          <div data-testid="module-progress" className="mt-5 max-w-md">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
              <span>Progress</span>
              <span>{mod.completed_count} of {mod.total_lessons} lessons complete</span>
            </div>
            <div className="h-1.5 bg-slate-100">
              <div className="h-1.5 bg-brand-orange" style={{ width: `${mod.total_lessons > 0 ? Math.round((mod.completed_count / mod.total_lessons) * 100) : 0}%` }} />
            </div>
          </div>
        )}

        {mod.unlocked && mod.module_complete && (
          <div data-testid="module-complete-panel" className="mt-8 bg-emerald-50 border border-emerald-200 p-6 flex items-start gap-4 max-w-3xl">
            <CheckCircle2 className="w-8 h-8 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-serif text-xl text-emerald-900 mb-1">Module complete</div>
              <p className="text-sm text-emerald-800">
                You've finished every lesson in this module. Your Certificate of Completion and Course Completion Record are issued once you finish all twelve modules.
              </p>
            </div>
          </div>
        )}

        {!mod.unlocked && (
          <div data-testid="module-gate" className="mt-8 bg-brand-blue text-white p-10">
            <div className="text-center mb-6">
              <Lock className="w-8 h-8 text-brand-yellow mx-auto mb-4" />
              <h3 className="font-serif text-2xl mb-3">Paid members only</h3>
              <p className="text-white/80 max-w-md mx-auto">Upgrade to unlock every lesson and editable template in this module.</p>
            </div>
            <UpgradePlans compact />
          </div>
        )}

        {mod.unlocked && (
          <>
            <h2 className="font-serif text-xl text-brand-blue mt-10 mb-4">Lessons</h2>
            {mod.lessons.length === 0 && (
              <div data-testid="module-lessons-empty" className="bg-white border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">Lessons for this module are coming soon.</div>
            )}
            <ol className="space-y-2">
              {mod.lessons.map((l, idx) => (
                <li key={l.slug}>
                  <div className="group flex items-start gap-4 bg-white border border-slate-200 hover:border-brand-orange transition-colors p-5">
                    <div className={`flex-shrink-0 w-8 h-8 border flex items-center justify-center text-xs font-bold ${l.completed ? "bg-brand-orange border-brand-orange text-white" : "border-slate-300 text-slate-500 group-hover:border-brand-orange"}`}>
                      {l.completed ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link to={`/member/toolbox/${mod.slug}/${l.slug}`} data-testid={`lesson-row-${l.slug}`}>
                        <div className="font-serif text-lg text-brand-blue mb-1 leading-snug hover:text-brand-orange">{l.title}</div>
                        {l.description && <div className="text-sm text-slate-600 line-clamp-2">{l.description}</div>}
                        <div className="text-xs text-slate-500 mt-2 flex items-center gap-3 flex-wrap">
                          {l.duration && <span>{l.duration}</span>}
                          <span className="flex items-center gap-1">{l.video_available
                            ? <><PlayCircle className="w-3.5 h-3.5 text-brand-orange" /> Video</>
                            : <><VideoOff className="w-3.5 h-3.5 text-slate-300" /> Video coming soon</>}
                          </span>
                          {l.document_count > 0 && <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {l.document_count} download{l.document_count === 1 ? "" : "s"}</span>}
                        </div>
                      </Link>
                      {mod.is_admin && (
                        <AdminUrlEditor
                          testid={`admin-video-editor-${l.slug}`}
                          label="Video URL"
                          initialValue={l.video_url || ""}
                          onSave={async (val) => {
                            await axios.put(`${API}/toolbox/admin/${mod.slug}/lessons/${l.slug}/video-url`, { url: val });
                            toast.success("Video URL saved.");
                            load();
                          }}
                        />
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>

            {mod.documents.length > 0 && (
              <>
                <h2 className="font-serif text-xl text-brand-blue mt-10 mb-4">Module downloads</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {mod.documents.map((d) => (
                    <DocumentDownloadTile
                      key={d.slug}
                      moduleSlug={mod.slug}
                      lessonSlug={null}
                      doc={d}
                      isAdmin={mod.is_admin}
                      onSaved={load}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </MemberShell>
  );
}

/* ------------------------------ Lesson detail ------------------------------ */

export function ToolboxLesson() {
  const { moduleSlug, lessonSlug } = useParams();
  const navigate = useNavigate();
  const [mod, setMod] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/toolbox/${moduleSlug}`)
    .then(({ data }) => {
      setMod(data);
      const found = (data.lessons || []).find((l) => l.slug === lessonSlug);
      if (!found) {
        toast.error("Lesson not found.");
        navigate(`/member/toolbox/${moduleSlug}`, { replace: true });
        return;
      }
      setLesson(found);
    })
    .catch(() => toast.error("Could not load lesson."));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [moduleSlug, lessonSlug]);

  const toggleComplete = async () => {
    setBusy(true);
    try {
      if (lesson.completed) {
        await axios.delete(`${API}/toolbox/${moduleSlug}/lessons/${lessonSlug}/complete`);
        toast.success("Marked incomplete.");
      } else {
        await axios.post(`${API}/toolbox/${moduleSlug}/lessons/${lessonSlug}/complete`);
        toast.success("Lesson complete.");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update progress.");
    } finally { setBusy(false); }
  };

  if (!mod || !lesson) return <MemberShell active="/member/toolbox"><div className="text-slate-500">Loading…</div></MemberShell>;

  return (
    <MemberShell active="/member/toolbox">
      <div data-testid={`lesson-page-${lesson.slug}`}>
        <Link to={`/member/toolbox/${mod.slug}`} data-testid="back-to-module" className="text-xs uppercase tracking-widest font-semibold text-slate-500 hover:text-brand-orange inline-flex items-center gap-1.5">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to {mod.title}
        </Link>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest font-bold text-brand-orange">Module {mod.number}</span>
          {lesson.duration && <span className="text-xs text-slate-500">{lesson.duration}</span>}
          {lesson.completed && <span data-testid="lesson-completed-badge" className="text-[10px] uppercase tracking-widest font-bold bg-emerald-100 text-emerald-800 px-2 py-1 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Completed</span>}
        </div>
        <h1 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight mt-4">{lesson.title}</h1>
        {lesson.description && <p className="text-slate-600 mt-4 max-w-3xl leading-relaxed">{lesson.description}</p>}

        <div data-testid="lesson-video-wrap" className="mt-8 aspect-video bg-slate-900 relative overflow-hidden">
          {lesson.video_id ? (
            <iframe
              data-testid="lesson-video-iframe"
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube-nocookie.com/embed/${lesson.video_id}?rel=0&modestbranding=1`}
              title={lesson.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70">
              <VideoOff className="w-10 h-10 mb-3 text-white/40" />
              <div className="text-sm">Video coming soon</div>
              <div className="text-xs text-white/50 mt-1">Andrea will publish this lesson to YouTube shortly.</div>
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          {(lesson.question_count || 0) === 0 && (
            <button data-testid="toggle-complete-btn" onClick={toggleComplete} disabled={busy}
              className={`inline-flex items-center gap-2 px-5 py-3 font-semibold text-sm ${lesson.completed ? "border border-slate-300 text-slate-600 hover:text-brand-orange hover:border-brand-orange" : "bg-brand-orange text-white hover:-translate-y-0.5 transition-transform"}`}>
              {busy
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                : lesson.completed
                  ? <><Circle className="w-4 h-4" /> Mark as incomplete</>
                  : <><CheckCircle2 className="w-4 h-4" /> Mark as complete</>}
            </button>
          )}
          {lesson.last_attempt && (
            <div data-testid="last-attempt-badge" className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold px-3 py-2 border ${lesson.last_attempt.passed ? "border-emerald-200 text-emerald-800 bg-emerald-50" : "border-amber-200 text-amber-800 bg-amber-50"}`}>
              <HelpCircle className="w-3.5 h-3.5" /> Last attempt: {lesson.last_attempt.score}/{lesson.last_attempt.total}
              {lesson.last_attempt.passed ? " · passed" : ""}
            </div>
          )}
        </div>

        {(lesson.questions || []).length > 0 && (
          <QuizPanel
            moduleSlug={mod.slug}
            lessonSlug={lesson.slug}
            questions={lesson.questions}
            completed={lesson.completed}
            lastAttempt={lesson.last_attempt}
            onPassed={load}
          />
        )}

        {mod.is_admin && (
          <AdminQuestionsEditor moduleSlug={mod.slug} lessonSlug={lesson.slug} onSaved={load} />
        )}

        {(lesson.documents || []).length > 0 && (
          <>
            <h2 className="font-serif text-xl text-brand-blue mt-10 mb-4">Lesson downloads</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {lesson.documents.map((d) => (
                <DocumentDownloadTile
                  key={d.slug}
                  moduleSlug={mod.slug}
                  lessonSlug={lesson.slug}
                  doc={d}
                  isAdmin={mod.is_admin}
                  onSaved={load}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </MemberShell>
  );
}

/* ------------------------------ Quiz Panel ------------------------------ */

function QuizPanel({ moduleSlug, lessonSlug, questions, completed, lastAttempt, onPassed }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  // Show the "passed" summary only when the lesson is completed AND the most recent attempt (if any) also passed.
  // This prevents contradictory UI where a failing retake still shows "Knowledge check passed".
  const lastAttemptPassed = lastAttempt ? !!lastAttempt.passed : true;
  const [showRetake, setShowRetake] = useState(!(completed && lastAttemptPassed));

  const submit = async () => {
    if (Object.keys(answers).length !== questions.length) {
      toast.error("Answer every question before submitting.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/toolbox/${moduleSlug}/lessons/${lessonSlug}/quiz`,
        { answers },
      );
      setResult(data);
      if (data.passed) {
        toast.success("Passed - lesson complete!");
        onPassed && onPassed();
      } else {
        toast.error(`${data.score}/${data.total} - review the explanations and try again.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit quiz.");
    } finally { setBusy(false); }
  };

  const retake = () => { setAnswers({}); setResult(null); setShowRetake(true); };

  if (completed && lastAttemptPassed && !showRetake && !result) {
    return (
      <div data-testid="quiz-passed-summary" className="mt-8 bg-emerald-50 border border-emerald-200 p-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-700" />
          <div>
            <div className="font-semibold text-emerald-900">Knowledge check passed</div>
            <div className="text-xs text-emerald-800">You've already completed this lesson.</div>
          </div>
        </div>
        <button data-testid="quiz-retake-btn" onClick={retake} className="text-xs uppercase tracking-widest font-semibold text-emerald-800 hover:text-emerald-950">Retake</button>
      </div>
    );
  }

  return (
    <div data-testid="quiz-panel" className="mt-10 border-t border-slate-200 pt-8">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="font-serif text-2xl text-brand-blue">Knowledge check</h2>
        <span className="text-xs text-slate-500 uppercase tracking-widest">{questions.length} questions · all correct to complete</span>
      </div>
      <p className="text-sm text-slate-600 mb-6">Answer all five correctly to mark this lesson complete.</p>

      <ol className="space-y-6">
        {questions.map((q, qi) => {
          const r = result?.results?.find((x) => x.id === q.id);
          return (
            <li key={q.id} data-testid={`quiz-q-${q.id}`} className="bg-white border border-slate-200 p-5">
              <div className="font-semibold text-slate-900 mb-3">{qi + 1}. {q.prompt}</div>
              <div className="space-y-2">
                {q.options.map((opt, oi) => {
                  const selected = answers[q.id] === oi;
                  const isCorrectResult = r && r.correct_index === oi;
                  const isWrongPick = r && r.submitted === oi && !r.is_correct;
                  const cls = r
                    ? isCorrectResult
                      ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                      : isWrongPick
                        ? "border-red-400 bg-red-50 text-red-900"
                        : "border-slate-200 text-slate-600"
                    : selected
                      ? "border-brand-orange bg-orange-50 text-brand-blue"
                      : "border-slate-200 hover:border-brand-orange text-slate-700";
                  return (
                    <label key={oi} data-testid={`quiz-q-${q.id}-opt-${oi}`}
                      className={`flex items-start gap-3 border p-3 cursor-pointer transition-colors ${cls}`}>
                      <input
                        type="radio" name={q.id} value={oi}
                        checked={selected} disabled={!!result}
                        onChange={() => setAnswers({ ...answers, [q.id]: oi })}
                        className="mt-1 accent-brand-orange"
                      />
                      <span className="text-sm">{opt.text}</span>
                      {r && isCorrectResult && <CheckCircle2 className="w-4 h-4 text-emerald-700 ml-auto mt-0.5 flex-shrink-0" />}
                      {r && isWrongPick && <XCircle className="w-4 h-4 text-red-600 ml-auto mt-0.5 flex-shrink-0" />}
                    </label>
                  );
                })}
              </div>
              {r?.explanation && (
                <div data-testid={`quiz-q-${q.id}-explanation`} className={`mt-3 text-xs px-3 py-2 border-l-2 ${r.is_correct ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-red-400 bg-red-50 text-red-900"}`}>
                  <span className="font-semibold uppercase tracking-widest text-[10px] mr-2">Why</span>
                  {r.explanation}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      <div className="mt-6 flex flex-wrap gap-3">
        {!result && (
          <button data-testid="quiz-submit-btn" onClick={submit} disabled={busy}
            className="inline-flex items-center gap-2 bg-brand-orange text-white px-5 py-3 font-semibold text-sm hover:-translate-y-0.5 transition-transform">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Grading…</> : <>Submit answers <ArrowRight className="w-4 h-4" /></>}
          </button>
        )}
        {result && !result.passed && (
          <button data-testid="quiz-try-again-btn" onClick={retake}
            className="inline-flex items-center gap-2 bg-brand-orange text-white px-5 py-3 font-semibold text-sm">
            Try again <ArrowRight className="w-4 h-4" />
          </button>
        )}
        {result && (
          <div data-testid="quiz-result-summary" className={`inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 border ${result.passed ? "border-emerald-500 text-emerald-800 bg-emerald-50" : "border-red-400 text-red-800 bg-red-50"}`}>
            Score: {result.score}/{result.total} {result.passed ? " · passed" : " · try again"}
          </div>
        )}
      </div>
    </div>
  );
}


/* ------------------------------ Admin Questions JSON editor ------------------------------ */

function AdminQuestionsEditor({ moduleSlug, lessonSlug, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const openEditor = async () => {
    setOpen(true); setErr(null); setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/toolbox/admin/${moduleSlug}/lessons/${lessonSlug}/questions`,
      );
      setText(JSON.stringify(data.questions || [], null, 2));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load questions.");
    } finally { setLoading(false); }
  };

  const save = async () => {
    setBusy(true); setErr(null);
    let parsed;
    try {
      parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) throw new Error("Root must be an array of questions.");
    } catch (e) {
      setErr(`Invalid JSON: ${e.message}`);
      setBusy(false);
      return;
    }
    try {
      await axios.put(
        `${API}/toolbox/admin/${moduleSlug}/lessons/${lessonSlug}/questions`,
        { questions: parsed },
      );
      toast.success("Quiz questions saved.");
      setOpen(false);
      if (onSaved) onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const template = JSON.stringify([
    {
      id: "q1",
      prompt: "Example prompt?",
      options: ["Option A", "Option B", "Option C", "Option D"],
      correct: 0,
      explanation: "Optional explanation shown after submission.",
    },
  ], null, 2);

  if (!open) {
    return (
      <div className="mt-8 border border-dashed border-brand-yellow bg-yellow-50 p-4 flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Pencil className="w-4 h-4 text-brand-blue mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-brand-blue">Admin: edit knowledge check</div>
            <div className="text-xs text-slate-600">Update MCQ questions as JSON without redeploying.</div>
          </div>
        </div>
        <button data-testid="admin-questions-edit-btn" onClick={openEditor}
          className="text-xs uppercase tracking-widest font-semibold text-brand-blue border border-brand-blue px-3 py-2 hover:bg-brand-blue hover:text-white transition-colors">
          Edit questions JSON
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-questions-editor" className="mt-8 border border-brand-yellow bg-yellow-50 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-brand-blue">Edit knowledge-check questions</div>
        <button data-testid="admin-questions-close" onClick={() => setOpen(false)}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"><X className="w-3 h-3" /> Close</button>
      </div>
      <p className="text-xs text-slate-600 mb-3">
        Root JSON must be an array. Each question needs <code>prompt</code>, <code>options</code> (≥2), and a zero-based <code>correct</code> index.
      </p>
      {loading ? (
        <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading current questions…</div>
      ) : (
        <>
          <textarea
            data-testid="admin-questions-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
            className="w-full h-96 font-mono text-xs p-3 border border-slate-300 bg-white focus:outline-none focus:border-brand-blue"
          />
          {err && <div data-testid="admin-questions-error" className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 p-2">{err}</div>}
          <div className="mt-3 flex flex-wrap gap-3 items-center">
            <button data-testid="admin-questions-save" onClick={save} disabled={busy}
              className="inline-flex items-center gap-2 bg-brand-orange text-white px-4 py-2 font-semibold text-sm">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save questions</>}
            </button>
            <button data-testid="admin-questions-template" onClick={() => setText(template)}
              className="text-xs uppercase tracking-widest font-semibold text-slate-600 hover:text-brand-blue">
              Insert template
            </button>
          </div>
        </>
      )}
    </div>
  );
}


/* ------------------------------ Admin URL editor ------------------------------ */

function AdminUrlEditor({ testid, label, initialValue, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(initialValue || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(initialValue || ""); }, [initialValue]);

  const save = async () => {
    setBusy(true);
    try {
      await onSave(val);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <button data-testid={testid} onClick={(e) => { e.preventDefault(); setEditing(true); }}
        className="mt-2 text-[11px] uppercase tracking-widest font-semibold text-brand-orange inline-flex items-center gap-1 hover:opacity-80">
        <Pencil className="w-3 h-3" /> {label}
      </button>
    );
  }
  return (
    <div className="mt-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <input
        data-testid={`${testid}-input`}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Paste URL"
        className="flex-1 text-xs px-2 py-1.5 border border-slate-300 focus:outline-none focus:border-brand-blue"
        onClick={(e) => e.preventDefault()}
      />
      <button data-testid={`${testid}-save`} onClick={(e) => { e.preventDefault(); save(); }} disabled={busy}
        className="text-xs px-2 py-1.5 bg-brand-orange text-white inline-flex items-center gap-1">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
      </button>
      <button data-testid={`${testid}-cancel`} onClick={(e) => { e.preventDefault(); setEditing(false); setVal(initialValue || ""); }}
        className="text-xs px-2 py-1.5 border border-slate-300 text-slate-500 inline-flex items-center gap-1">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

/* ------------------------------ Shared: download tile ------------------------------ */

function DocumentDownloadTile({ moduleSlug, lessonSlug, doc, isAdmin, onSaved }) {
  const [busy, setBusy] = useState(false);
  const start = async () => {
    if (!doc.has_source) {
      toast.info("This document is being prepared and will appear here shortly.");
      return;
    }
    setBusy(true);
    try {
      const url = lessonSlug
        ? `${API}/toolbox/${moduleSlug}/lessons/${lessonSlug}/documents/${doc.slug}/link`
        : `${API}/toolbox/${moduleSlug}/documents/${doc.slug}/link`;
      const { data } = await axios.post(url);
      window.open(`${process.env.REACT_APP_BACKEND_URL}${data.download_url}`, "_blank", "noopener,noreferrer");
      toast.success("Opening download…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start download.");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid={`doc-tile-${doc.slug}`} className="bg-white border border-slate-200 hover:border-brand-orange transition-colors p-4">
      <button onClick={start} disabled={busy} className="w-full text-left flex items-start gap-3">
        <FileText className="w-5 h-5 text-brand-orange flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 leading-snug">{doc.title}</div>
          <div className="text-xs text-slate-500 mt-1 uppercase tracking-widest">
            {doc.has_source ? doc.kind : "Coming soon"}
          </div>
        </div>
        {busy
          ? <Loader2 className="w-4 h-4 animate-spin text-slate-400 flex-shrink-0" />
          : doc.has_source
            ? <Download className="w-4 h-4 text-slate-400 flex-shrink-0" />
            : <CheckCircle2 className="w-4 h-4 text-slate-300 flex-shrink-0" />}
      </button>
      {isAdmin && (
        <AdminUrlEditor
          testid={`admin-doc-editor-${doc.slug}`}
          label="Set download URL"
          initialValue={doc.external_url || ""}
          onSave={async (val) => {
            const url = lessonSlug
              ? `${API}/toolbox/admin/${moduleSlug}/lessons/${lessonSlug}/documents/${doc.slug}/external-url`
              : `${API}/toolbox/admin/${moduleSlug}/documents/${doc.slug}/external-url`;
            await axios.put(url, { url: val });
            toast.success("Download URL saved.");
            if (onSaved) onSaved();
          }}
        />
      )}
    </div>
  );
}
